/**
 * BruceSuper – Tiered Gift With Purchase (GWP)
 *
 * Design rules:
 *  - No polling loops
 *  - Single event listener with debounce
 *  - One in-flight request at a time (syncing lock)
 *  - Only fires on explicit cart change events from the Warehouse theme
 */

(function () {

  // ─── KILL SWITCH ────────────────────────────────────────────────────────────
  var ENABLED = false;
  if (!ENABLED) return;

  // ─── CONFIG ─────────────────────────────────────────────────────────────────
  // Thresholds in cents (AUD). variantId = Shopify variant ID for the gift product.
  // Set variantId to null to leave a tier as a placeholder (it will be skipped).
  var TIERS = [
    { threshold:   100, variantId: 42076459991237, tag: 'gwp_tier1' },  // Every order → Prime Energy
    { threshold:  5000, variantId: 40592552329413, tag: 'gwp_tier2' },  // $50+        → Swedish Fish / Sour Patch
    { threshold:  9900, variantId: 31185075732573, tag: 'gwp_tier3' },  // $99+        → Surprise Gummy
    { threshold: 15000, variantId: 45323925913797, tag: 'gwp_tier4' },  // $150+       → Candy Socks
    { threshold: 20000, variantId: 41888709050565, tag: 'gwp_tier5' },  // $200+       → New item
    { threshold: 25000, variantId: 45756843720901, tag: 'gwp_tier6' },  // $250+       → Reeses Hearts
  ];

  // Qualify on pre-discount subtotal (false) or post-discount total (true)
  var QUALIFY_AFTER_DISCOUNTS = false;

  // ─── INTERNALS ───────────────────────────────────────────────────────────────
  var syncing = false;
  var debounceTimer = null;
  var DEBOUNCE_MS = 150;

  function fetchCart() {
    return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); });
  }

  function addGift(variantId, tag) {
    return fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1, properties: { _gwp: tag } })
    }).then(function (r) { return r.json(); });
  }

  function removeLine(lineKey) {
    return fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: 0 })
    }).then(function (r) { return r.json(); });
  }

  function giftItems(cart) {
    return cart.items.filter(function (item) {
      return item.properties && item.properties._gwp;
    });
  }

  function qualifyingTotal(cart) {
    return QUALIFY_AFTER_DISCOUNTS ? cart.total_price : cart.items_subtotal_price;
  }

  function reachedTierTags(totalCents) {
    return TIERS
      .filter(function (t) { return t.variantId !== null && totalCents >= t.threshold; })
      .map(function (t) { return t.tag; });
  }

  function tierByTag(tag) {
    return TIERS.find(function (t) { return t.tag === tag; }) || null;
  }

  async function syncGifts() {
    if (syncing) return;
    syncing = true;

    try {
      var cart = await fetchCart();
      var total = qualifyingTotal(cart);
      var shouldHave = reachedTierTags(total);
      var currentGifts = giftItems(cart);

      // Remove gifts for tiers no longer reached
      for (var i = 0; i < currentGifts.length; i++) {
        var g = currentGifts[i];
        if (shouldHave.indexOf(g.properties._gwp) === -1) {
          await removeLine(g.key);
        }
      }

      // Fetch fresh cart after removals
      cart = await fetchCart();
      total = qualifyingTotal(cart);
      var presentTags = giftItems(cart).map(function (g) { return g.properties._gwp; });
      var nowShould = reachedTierTags(total);

      // Add any missing gifts
      for (var j = 0; j < nowShould.length; j++) {
        var tag = nowShould[j];
        if (presentTags.indexOf(tag) === -1) {
          var tier = TIERS.find(function (t) { return t.tag === tag; });
          if (tier && tier.variantId) {
            await addGift(tier.variantId, tag);
          }
        }
      }

      // Enforce qty = 1 on any gift that somehow got bumped
      cart = await fetchCart();
      var finalGifts = giftItems(cart);
      for (var k = 0; k < finalGifts.length; k++) {
        if (finalGifts[k].quantity > 1) {
          await fetch('/cart/change.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: finalGifts[k].key, quantity: 1 })
          });
        }
      }

      // Tell the theme to re-render the cart UI
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

    } catch (e) {
      console.error('[GWP] sync error:', e);
    } finally {
      syncing = false;
    }
  }

  function scheduleSync() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(syncGifts, DEBOUNCE_MS);
  }

  // ─── HIDE ZERO-VALUE DISCOUNT LABELS ────────────────────────────────────────
  // Shopify tags the qualifying "buy" item with the discount name at -$0.00.
  // We hide any discount badge whose amount text contains "$0.00".
  function hideZeroDiscountBadges() {
    document.querySelectorAll('.line-item__discount, .mini-cart__discount').forEach(function (el) {
      if (el.textContent.indexOf('$0.00') !== -1) {
        el.style.display = 'none';
      } else {
        el.style.display = '';
      }
    });
  }

  // ─── CHECKOUT INTERCEPTION ───────────────────────────────────────────────────
  // Run a final sync before checkout so no earned gifts are missed.
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('button[name="checkout"], input[name="checkout"], a[href="/checkout"]');
    if (!btn) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    clearTimeout(debounceTimer);
    function waitThenSync() {
      if (syncing) { setTimeout(waitThenSync, 100); return; }
      syncGifts().then(function () { window.location.href = '/checkout'; });
    }
    waitThenSync();
  }, true);

  // ─── EVENT LISTENERS ─────────────────────────────────────────────────────────
  document.addEventListener('cart:updated', scheduleSync);
  document.addEventListener('product:added', scheduleSync);
  // Watch stable parent containers (never replaced by AJAX) for any cart re-renders
  var cartObserver = new MutationObserver(hideZeroDiscountBadges);
  document.addEventListener('DOMContentLoaded', function () {
    var targets = [
      document.querySelector('#main'),
      document.querySelector('.mini-cart')
    ];
    targets.forEach(function (el) {
      if (el) cartObserver.observe(el, { childList: true, subtree: true });
    });
  });

  // Also run once on page load to correct any stale gift state
  document.addEventListener('DOMContentLoaded', function () {
    syncGifts();
    hideZeroDiscountBadges();
  });

})();
